import { useState } from 'react';
import { Loader2, AlertTriangle, ReceiptText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const VERDICT_LABEL = {
  payload_has_fee_stored_zero: 'ingestion dropped a real fee',
  payload_genuinely_zero: 'no fee in payload',
  payload_has_fee_stored_correctly: 'fee ingested correctly',
  unresolvable: 'unresolvable',
};

const VERDICT_VARIANT = {
  payload_has_fee_stored_zero: 'destructive',
  payload_genuinely_zero: 'outline',
  payload_has_fee_stored_correctly: 'default',
  unresolvable: 'secondary',
};

function VerdictBadge({ verdict }) {
  return (
    <Badge variant={VERDICT_VARIANT[verdict] || 'outline'}>
      {VERDICT_LABEL[verdict] || verdict}
    </Badge>
  );
}

function SummaryCounts({ summary }) {
  if (!summary) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(summary).map(([verdict, count]) => (
        <Badge key={verdict} variant={VERDICT_VARIANT[verdict] || 'outline'}>
          {count} {VERDICT_LABEL[verdict] || verdict}
        </Badge>
      ))}
    </div>
  );
}

function ReservationTable({ reservations }) {
  if (!reservations || reservations.length === 0) {
    return <p className="text-sm text-muted-foreground">No reservations sampled.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border">
        <thead className="bg-muted/50">
          <tr>
            <th className="text-left p-2">Property / Guest</th>
            <th className="text-left p-2">Channel id (raw)</th>
            <th className="text-right p-2">Stored total / commission / net</th>
            <th className="text-left p-2">Nonzero fee fields (raw payload below)</th>
            <th className="text-left p-2">Verdict</th>
          </tr>
        </thead>
        <tbody>
          {reservations.map((r) => (
            <tr key={r.hostaway_id} className="border-t align-top">
              <td className="p-2">
                {r.matched_named_target && (
                  <div className="text-emerald-600 font-medium mb-0.5">{r.matched_named_target}</div>
                )}
                <div>{r.property_name || <em>(unknown)</em>}</div>
                <div className="text-muted-foreground">{r.guest_name || <em>(no guest name)</em>}</div>
                <div className="font-mono text-muted-foreground">{r.hostaway_id}</div>
              </td>
              <td className="p-2 font-mono">
                {r.channel_id}
                {r.raw_channel_name && (
                  <div className="text-muted-foreground">{r.raw_channel_name}</div>
                )}
              </td>
              <td className="p-2 text-right font-mono">
                {r.stored?.total_price ?? '—'} / {r.stored?.channel_commission ?? '—'} / {r.stored?.net_revenue ?? '—'}
              </td>
              <td className="p-2 max-w-sm">
                {r.unresolvable_reason ? (
                  <span className="font-mono text-amber-600">{r.unresolvable_reason}</span>
                ) : Object.keys(r.nonzero_monetary_candidates || {}).length === 0 ? (
                  <em className="not-italic text-muted-foreground">(no nonzero fee-shaped field)</em>
                ) : (
                  <ul className="font-mono space-y-0.5">
                    {Object.entries(r.nonzero_monetary_candidates).map(([k, v]) => (
                      <li key={k}>
                        {k}: <span className="font-medium">{v}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {Object.keys(r.fee_shaped_fields || {}).length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-muted-foreground">raw payload</summary>
                    <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-all text-[10px] bg-muted/40 p-1.5 rounded">
                      {JSON.stringify(r.fee_shaped_fields, null, 1)}
                    </pre>
                  </details>
                )}
              </td>
              <td className="p-2"><VerdictBadge verdict={r.verdict} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BookingComFeeAuditPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.bookingComFeeAudit();
      setResult(data);
      if (data.status !== 'ok') {
        toast.error(data.error || data.status);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;

  return (
    <Card data-testid="booking-com-fee-audit-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ReceiptText className="h-5 w-5 text-blue-600" />
          Booking.com Zero-Commission Fee Audit (Read-Only)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Samples stored booking_com reservations (Morrison St / Francesco Mirani and Jacksons
          Close / Eric Gray by name if resolvable) plus an airbnb contrast set, re-fetches each
          one's raw live Hostaway payload, and dumps every fee-shaped field found &mdash; no
          pre-filtering to the field names normalize_reservation already reads. Diagnoses whether
          net == gross on those booking_com rows is an ingestion drop or a genuine payload gap.
          Read-only, no writes, no fix applied.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button size="sm" variant="outline" onClick={run} disabled={loading} data-testid="booking-com-fee-audit-run">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ReceiptText className="h-3.5 w-3.5 mr-1.5" />}
          Run audit
        </Button>

        {r && r.status !== 'ok' && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {r.error || r.status}
          </div>
        )}

        {r && r.status === 'ok' && (
          <div className="space-y-4" data-testid="booking-com-fee-audit-result">
            <div className="text-sm border rounded-lg p-3 bg-muted/30" data-testid="booking-com-fee-audit-headline">
              {r.headline}
            </div>

            <div className="flex flex-wrap gap-2 text-xs items-center">
              <Badge variant="outline">
                airbnb fee field = {r.fee_field_by_channel?.airbnb || 'none observed'}
              </Badge>
              <Badge variant="outline">
                booking_com fee field = {r.fee_field_by_channel?.booking_com || 'none observed'}
              </Badge>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-1.5">
                Booking.com ({r.booking_com?.sampled ?? 0} sampled)
              </h4>
              <SummaryCounts summary={r.booking_com?.summary} />
              <div className="mt-2">
                <ReservationTable reservations={r.booking_com?.reservations} />
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-1.5">
                Airbnb contrast set ({r.airbnb_contrast?.sampled ?? 0} sampled)
              </h4>
              <SummaryCounts summary={r.airbnb_contrast?.summary} />
              <div className="mt-2">
                <ReservationTable reservations={r.airbnb_contrast?.reservations} />
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
